import {
  Activity,
  Bot,
  Building2,
  ClipboardList,
  CreditCard,
  FileClock,
  Gauge,
  Globe2,
  KeyRound,
  Languages,
  LogOut,
  Menu,
  Moon,
  Settings,
  ServerCog,
  ShieldCheck,
  UserRound,
  UsersRound,
  Sun,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "../router.js";
import { useAuth } from "../auth.js";
import { useLocale } from "../i18n.js";

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useLocale();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("lv_theme") === "dark");
  const location = useLocation();
  useEffect(() => setSidebarOpen(false), [location.pathname]);
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("lv_theme", dark ? "dark" : "light");
  }, [dark]);
  const isPlatform =
    user?.platformRoleCode === "platform_super_admin" ||
    user?.platformRoleCode === "platform_security_analyst";
  const nav = [
    { to: "/", label: t("overview"), icon: Gauge },
    { to: "/organizations", label: t("organizations"), icon: Building2 },
    { to: "/sites", label: t("sites"), icon: Globe2 },
    { to: "/billing", label: t("billing"), icon: CreditCard },
    ...(isPlatform
      ? [
          ...(user?.platformRoleCode === "platform_super_admin"
            ? [{ to: "/users", label: t("users"), icon: UsersRound }]
            : []),
          { to: "/events", label: t("events"), icon: Activity },
          { to: "/risk", label: t("risk"), icon: ShieldCheck },
          { to: "/rules", label: t("rules"), icon: ClipboardList },
          { to: "/ai", label: t("ai"), icon: Bot },
          { to: "/settings", label: t("settings"), icon: Settings },
          { to: "/operations", label: t("operations"), icon: ServerCog },
          { to: "/audit", label: t("audit"), icon: FileClock },
        ]
      : []),
  ];
  return (
    <div className="app-frame">
      <aside className={`sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="brand-block">
          <span className="brand-mark">
            <KeyRound size={19} />
          </span>
          <span>Lobster Verify</span>
        </div>
        <button
          className="sidebar-close icon-button"
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label={t("closeNavigation")}
        >
          <X />
        </button>
        <nav aria-label={t("primaryNavigation")}>
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <NavLink className="profile-line" to="/account/security">
            <span className="avatar">{user?.displayName.slice(0, 1).toUpperCase()}</span>
            <span>
              <strong>{user?.displayName}</strong>
              <small>{user?.email}</small>
            </span>
            <UserRound size={16} />
          </NavLink>
          <button type="button" className="text-button" onClick={() => void logout()}>
            <LogOut size={16} />
            {t("signOut")}
          </button>
        </div>
      </aside>
      {sidebarOpen ? (
        <button
          className="sidebar-scrim"
          type="button"
          aria-label={t("closeNavigation")}
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <div className="workspace">
        <header className="topbar">
          <button
            className="mobile-menu icon-button"
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t("openNavigation")}
          >
            <Menu />
          </button>
          <div className="topbar-context">
            {nav.find((item) => location.pathname === item.to)?.label ?? t("console")}
          </div>
          <div className="topbar-actions">
            <button
              className="icon-button"
              type="button"
              title={t("language")}
              aria-label={t("changeLanguage")}
              onClick={() => setLocale(locale === "en" ? "zh-CN" : "en")}
            >
              <Languages />
            </button>
            <button
              className="icon-button"
              type="button"
              title={t("theme")}
              aria-label={t("changeTheme")}
              onClick={() => setDark((value) => !value)}
            >
              {dark ? <Sun /> : <Moon />}
            </button>
          </div>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
